// File: packages/components/src/homepage/invest/InvestTeaserSection.tsx
import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Button } from '@dopomogai/ui';
import { Rocket } from 'lucide-react';

type Accent =
  | 'emerald' | 'violet' | 'blue' | 'cyan' | 'amber' | 'rose'
  | 'indigo' | 'purple' | 'teal' | 'orange' | 'lime' | 'fuchsia'
  | 'pink' | 'slate' | 'gray';

const accentMap = (accent: Accent = 'emerald') => {
  switch (accent) {
    case 'violet':  return { fill: 'bg-violet-400', stripe: 'bg-violet-400', dot: 'bg-violet-500', tint: 'bg-violet-50' };
    case 'blue':    return { fill: 'bg-blue-500',   stripe: 'bg-blue-400',   dot: 'bg-blue-500',   tint: 'bg-blue-50' };
    case 'cyan':    return { fill: 'bg-cyan-500',   stripe: 'bg-cyan-400',   dot: 'bg-cyan-500',   tint: 'bg-cyan-50' };
    case 'amber':   return { fill: 'bg-amber-500',  stripe: 'bg-amber-400',  dot: 'bg-amber-500',  tint: 'bg-amber-50' };
    case 'rose':    return { fill: 'bg-rose-500',   stripe: 'bg-rose-400',   dot: 'bg-rose-500',   tint: 'bg-rose-50' };
    case 'indigo':  return { fill: 'bg-indigo-500', stripe: 'bg-indigo-400', dot: 'bg-indigo-500', tint: 'bg-indigo-50' };
    case 'purple':  return { fill: 'bg-purple-500', stripe: 'bg-purple-400', dot: 'bg-purple-500', tint: 'bg-purple-50' };
    case 'teal':    return { fill: 'bg-teal-500',   stripe: 'bg-teal-400',   dot: 'bg-teal-500',   tint: 'bg-teal-50' };
    case 'orange':  return { fill: 'bg-orange-500', stripe: 'bg-orange-400', dot: 'bg-orange-500', tint: 'bg-orange-50' };
    case 'lime':    return { fill: 'bg-lime-500',   stripe: 'bg-lime-400',   dot: 'bg-lime-500',   tint: 'bg-lime-50' };
    case 'fuchsia': return { fill: 'bg-fuchsia-500',stripe: 'bg-fuchsia-400',dot: 'bg-fuchsia-500',tint: 'bg-fuchsia-50' };
    case 'pink':    return { fill: 'bg-pink-500',   stripe: 'bg-pink-400',   dot: 'bg-pink-500',   tint: 'bg-pink-50' };
    case 'slate':   return { fill: 'bg-slate-600',  stripe: 'bg-slate-400',  dot: 'bg-slate-600',  tint: 'bg-slate-50' };
    case 'gray':    return { fill: 'bg-gray-600',   stripe: 'bg-gray-400',   dot: 'bg-gray-600',   tint: 'bg-gray-50' };
    default:        return { fill: 'bg-emerald-500',stripe: 'bg-emerald-400',dot: 'bg-emerald-500',tint: 'bg-emerald-50' };
  }
};

export type InvestProgress = {
  current: number; // same units as goal, e.g., USD
  goal: number;
  unit?: string; // e.g., '$'
  supporters?: number;
  labelKey?: string; // i18n key for custom label
};

export type InvestTeaserSectionProps = {
  title?: string;
  titleKey?: string;
  subtitle?: string;
  subtitleKey?: string;
  progress?: InvestProgress;

  cta?: { label?: string; labelKey?: string; actionId: string };
  secondaryCtas?: { label?: string; labelKey?: string; actionId: string }[];

  onAction?: (actionId: string, payload?: any) => void;

  // Visuals
  accent?: Accent;            // affects progress fill, dots, (optional) stripe
  showTopStripe?: boolean;    // thin stripe on both cards
  className?: string;
};

function formatAmount(n: number, unit?: string) {
  if (!Number.isFinite(n)) return '';
  const fmt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
  return unit ? `${unit}${fmt}` : fmt;
}

export const InvestTeaserSection: React.FC<InvestTeaserSectionProps> = ({
  title,
  titleKey,
  subtitle,
  subtitleKey,
  progress,
  cta,
  secondaryCtas = [],
  onAction,
  accent = 'emerald',
  showTopStripe = true,
  className,
}) => {
  const { t } = useTranslation('homepage');
  const resolvedTitle = titleKey ? t(titleKey) : (title ?? 'Back the AI workforce');
  const resolvedSubtitle = subtitleKey ? t(subtitleKey) : (subtitle ?? 'Co‑own agents with non‑transferable units and share in upside. Live analytics and quarterly payouts from profits after costs.');

  const c = accentMap(accent);

  const pct =
    progress && progress.goal > 0
      ? Math.min(100, Math.round((progress.current / progress.goal) * 100))
      : 0;

  return (
    <section className={clsx('bg-white', className)}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid gap-6 md:grid-cols-2 items-stretch">
          {/* Left: campaign + CTA */}
          <div
            className={clsx(
              'relative border-2 border-black rounded-3xl bg-white p-6 md:p-8',
              'transition-all duration-200 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)]'
            )}
          >
            {showTopStripe && (
              <div aria-hidden="true" className={clsx('absolute left-0 right-0 top-0 h-1.5 rounded-t-3xl', c.stripe)} />
            )}

            <div className="flex items-start gap-3">
              <div className="shrink-0 mt-1">
                <Rocket size={24} className="text-slate-700" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900">{resolvedTitle}</h3>
                <p className="mt-3 text-slate-700 text-lg leading-relaxed">
                  {resolvedSubtitle}
                </p>
              </div>
            </div>

            {/* Progress */}
            {progress ? (
              <div className="mt-6">
                <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
                  <span>{progress.labelKey ? t(progress.labelKey) : 'Campaign progress'}</span>
                  <span>
                    {pct}% • {formatAmount(progress.current, progress.unit)} / {formatAmount(progress.goal, progress.unit)}
                    {typeof progress.supporters === 'number' ? ` • ${progress.supporters} supporters` : ''}
                  </span>
                </div>
                <div
                  className="w-full border-2 border-black rounded-full bg-slate-100 overflow-hidden"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={pct}
                  aria-label="Campaign progress"
                >
                  <div
                    className={clsx('h-3 transition-all duration-300', c.fill)}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            ) : null}

            <div className="mt-6 border-t border-slate-200" />

            {/* Primary CTA */}
            {cta ? (
              <div className="mt-6">
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  text={cta.labelKey ? t(cta.labelKey) : (cta.label ?? 'Explore Invest')}
                  onClick={() => onAction?.(cta.actionId)}
                />
              </div>
            ) : null}
          </div>

          {/* Right: perks list */}
          <div
            className={clsx(
              'relative border-2 border-black rounded-3xl p-6 md:p-8',
              'transition-all duration-200 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)]',
              c.tint // soft background tint
            )}
          >
            {showTopStripe && (
              <div aria-hidden="true" className={clsx('absolute left-0 right-0 top-0 h-1.5 rounded-t-3xl', c.stripe)} />
            )}

            <h4 className="text-xl md:text-2xl font-extrabold text-slate-900">What you get as a backer</h4>

            <ul className="mt-4 space-y-3 text-slate-800 text-lg">
              <li className="flex gap-3">
                <span className={clsx('mt-2 inline-block w-2.5 h-2.5 rounded-full', c.dot)} />
                Early access to agent launches and perks
              </li>
              <li className="flex gap-3">
                <span className={clsx('mt-2 inline-block w-2.5 h-2.5 rounded-full', c.dot)} />
                Quarterly profit share after costs
              </li>
              <li className="flex gap-3">
                <span className={clsx('mt-2 inline-block w-2.5 h-2.5 rounded-full', c.dot)} />
                Live analytics dashboard
              </li>
              <li className="flex gap-3">
                <span className={clsx('mt-2 inline-block w-2.5 h-2.5 rounded-full', c.dot)} />
                Impact: 33% of usage funds non‑profits
              </li>
            </ul>

            {secondaryCtas.length > 0 ? (
              <div className="mt-6">
                <Button
                  variant="secondary"
                  size="lg"
                  text={secondaryCtas[0].labelKey ? t(secondaryCtas[0].labelKey) : (secondaryCtas[0].label ?? 'Read details')}
                  onClick={() => onAction?.(secondaryCtas[0].actionId)}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InvestTeaserSection;
